import cn from 'classnames';
import React, {FC, useEffect, useRef, useState} from 'react';

import oneLedImg2 from '../../../../assets/images/devices/FWUPopup/1led@2x.jpg';
import oneLedImg3 from '../../../../assets/images/devices/FWUPopup/1led@3x.jpg';
import twoLedImg2 from '../../../../assets/images/devices/FWUPopup/2led@2x.jpg';
import twoLedImg3 from '../../../../assets/images/devices/FWUPopup/2led@3x.jpg';
import AppButton from '../../../components/Button/AppButton/AppButton';
import {ArticlePageLayout} from '../../../components/DataDisplay';
import {IconTickSvg} from '../../../components/Icons';
import {ContentWrapper, MaxWidthContainer} from '../../../components/Layout';
import PopupFullPage from '../../../components/Popup/PopupFullPage';
import * as localizationKeys from '../../../consts/localization/localizationKeys';
import ModalTypes from '../../../enums/app/modalTypes';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import useHideModalIfNoDevice from '../../../hooks/useHideModalIfNoDevice';
import analyticsService from '../../../services/analyticsService';
import {getLocalizedStrings} from '../../../services/localization/localizationService';
import {hideModalAction, showFWUAdditionalStepsGuidelAction, showFWUErrorModalAction} from '../../../state/ducks/modal';
import {dispatch} from '../../../state/store';
import SignedImage from '../../System/SignedImage';
import styles from '../FWULedCheckPopup/FWULedCheckPopup.module.scss';

const FWULedCheckPopup: FC<{modalType: ModalTypes}> = (props) => {
    const [selectedLedId, setSelectedLedId] = useState<undefined | string>(undefined);
    const ONE_LED_ID = '1L';
    const TWO_LED_ID = '2L';
    const isButtonDisabled = !selectedLedId;
    const isOneLedSelected = selectedLedId === ONE_LED_ID;
    const isTwoLedSelected = selectedLedId === TWO_LED_ID;
    const {modalType} = props;
    const localizedStrings = getLocalizedStrings();
    const popupRef: any = useRef();
    const oneLedSrcSet = [oneLedImg2, oneLedImg3];
    const twoLedSrcSet = [twoLedImg2, twoLedImg3];
    const onClose = (isCloseBtnClick?: boolean) => {
        if (isCloseBtnClick) {
            dispatch(hideModalAction(modalType));
        } else {
            const dispatchAction = () => dispatch(hideModalAction(modalType));

            popupRef.current.onPopupCloseWithAnimation(dispatchAction);
        }
    };

    useHideModalIfNoDevice(modalType);

    const onRadioButtonClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedLedId(event.target.id);
    };

    const onContinueClick = () => {
        if (isOneLedSelected) {
            onClose();
            dispatch(showFWUErrorModalAction());
        }
        if (isTwoLedSelected) {
            onClose();
            dispatch(showFWUAdditionalStepsGuidelAction());
        }
    };

    useDocumentTitle(localizedStrings[localizationKeys.ACCESSIBILITY_FEW_MORE_STEPS_PAGE_TITLE]);

    useEffect(() => {
        analyticsService.pushFewMoreSteps();
    }, []);

    return (
        <PopupFullPage ref={popupRef} isCloseByOverlay={false} onClose={() => onClose(true)}>
            <ContentWrapper>
                <MaxWidthContainer>
                    <ArticlePageLayout
                        containerClassName='pt-0'
                        titleKey={localizationKeys.SELECT_LED_TITLE}
                        descriptionKey={localizationKeys.SELECT_LED_DESCRIPTION}
                    />
                    <p className='mb-[40px] w-full font-bold'>
                        {localizedStrings[localizationKeys.SELECT_LED_QUESTION]}
                    </p>
                    <div className={styles.ChoicesContainer}>
                        <ChoiceItemTemplate
                            text={localizedStrings[localizationKeys.ONE_LED_TEXT]}
                            imgSrc={oneLedSrcSet}
                            id={ONE_LED_ID}
                            selectedLed={isOneLedSelected}
                            onClickAction={onRadioButtonClick}
                        />
                        <ChoiceItemTemplate
                            text={localizedStrings[localizationKeys.TWO_LED_TEXT]}
                            imgSrc={twoLedSrcSet}
                            id={TWO_LED_ID}
                            selectedLed={isTwoLedSelected}
                            onClickAction={onRadioButtonClick}
                        />
                    </div>
                    <AppButton
                        className='mb-[100px]'
                        primary
                        light
                        disabled={isButtonDisabled}
                        onClick={onContinueClick}
                    >
                        {localizedStrings[localizationKeys.LEGAL_DISCLAIMER_SCREEN_CONTINUE_BUTTON_TEXT]}
                    </AppButton>
                </MaxWidthContainer>
            </ContentWrapper>
        </PopupFullPage>
    );
};

interface IChoiceItemTemplateType {
    text: string;
    imgSrc: string | Array<string>;
    id: string;
    selectedLed: boolean;
    onClickAction: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ChoiceItemTemplate: FC<IChoiceItemTemplateType> = ({text, imgSrc, id, selectedLed, onClickAction}) => {
    return (
        <div className={styles.ChoiceItemContainer}>
            <label className={styles.ChoiceItem}>
                <input
                    type='radio'
                    name={id}
                    id={id}
                    aria-label={text}
                    onChange={onClickAction}
                    checked={selectedLed}
                />
                <SignedImage defaultImage={imgSrc} imgClassName={cn(styles.Image)} />
                <div className={styles.RoundConfirmMark}>
                    <IconTickSvg className={styles.SvgContainer} />
                </div>
            </label>
            <p className={cn(styles.LedDescription, selectedLed ? styles.LedDescriptionSelected : null)}>{text}</p>
        </div>
    );
};

export default FWULedCheckPopup;
